/**
 * Slot Check Engagement chart PNG pack — dark SCE chrome canvas charts + STORE zip.
 * Pure helpers (no React). No new deps.
 *
 * Zip: SCE_{from}_{to}_charts.zip
 * Files: SCE_{from}_{to}_G{n}.png
 */
import { downloadBlob } from './scePdf';

export const PIE_COLORS = [
  '#5B8FF9',
  '#F6BD16',
  '#F6903D',
  '#61DDAA',
  '#7262fd',
  '#78D3F8',
  '#9661BC',
  '#008685',
];

export type SceChartBandVals = {
  uniques: number;
  sessions: number;
  created: number;
  linked: number;
  avg: number;
};

export type SceChartMonthPoint = {
  month: string;
  uniques?: number;
  sessions?: number;
  created?: number;
  linked?: number;
  avg?: number;
  byBand: Record<string, SceChartBandVals>;
};

export type SceChartMixSlice = { name: string; value: number; color: string };

export type SceChartPngInput = {
  scopeLabel?: string;
  fromMonth: string;
  toMonth: string;
  ageBands: string[];
  bandColors: string[];
  monthSeries: SceChartMonthPoint[];
  g5Month: string;
  /** G5 views: Age / Gender / Gender counts by Age Range */
  g5Views: Array<{ key: string; title: string; mix: SceChartMixSlice[] }>;
};

const W = 960;
const H = 540;
const BG = '#121820';
const CARD = '#1a2332';
const TEXT = '#ffffff';
const MUTED = 'rgba(255,255,255,0.55)';
const GRID = 'rgba(255,255,255,0.08)';
const GOLD = '#F6BD16';

/** G1–G4 bottom bands (PE_CHART_PNG_POLISH): legend / gap / axis — no overlap. */
const TITLE_TOP = 72;
const LEGEND_BAND = 64;
const AXIS_GAP = 16;
const AXIS_BAND = 44;
const BOTTOM_RESERVE = LEGEND_BAND + AXIS_GAP + AXIS_BAND; // 124

function makeCanvas(height: number = H): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = height;
  return c;
}

function paintChrome(
  ctx: CanvasRenderingContext2D,
  title: string,
  ageBands: string[],
  bandColors: string[]
) {
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, ctx.canvas.height);
  const ch = ctx.canvas.height;
  ctx.fillStyle = CARD;
  ctx.fillRect(16, 16, W - 32, ch - 32);

  ctx.fillStyle = GOLD;
  ctx.fillRect(32, 36, 8, 8);
  ctx.fillStyle = TEXT;
  ctx.font = 'bold 18px Inter, system-ui, sans-serif';
  ctx.fillText(title, 48, 46);

  // 2×4 wrap for 8 AGE_BANDS inside dedicated legend band (~64px)
  if (ageBands.length) {
    const cols = 4;
    const rows = Math.ceil(ageBands.length / cols);
    const colW = (W - 80) / cols;
    const rowH = 22;
    const legendBottom = ch - 28;
    const legendTop = legendBottom - (rows - 1) * rowH;
    ctx.font = '12px Inter, system-ui, sans-serif';
    ageBands.forEach((band, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const lx = 40 + col * colW;
      const ly = legendTop + row * rowH;
      const color = bandColors[i] || PIE_COLORS[i % PIE_COLORS.length];
      ctx.fillStyle = color;
      ctx.fillRect(lx, ly - 8, 10, 10);
      ctx.fillStyle = MUTED;
      ctx.fillText(band, lx + 14, ly);
    });
  }
}

export function niceMax(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / pow;
  const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return nice * pow;
}

/** Locked SoT: yMax = niceMax(peak * 1.10); peak 0 → 1. */
export function sceYMax(peak: number): number {
  if (!Number.isFinite(peak) || peak <= 0) return 1;
  return niceMax(peak * 1.1);
}

function drawStackedBars(
  ctx: CanvasRenderingContext2D,
  title: string,
  series: SceChartMonthPoint[],
  ageBands: string[],
  bandColors: string[],
  metric: keyof SceChartBandVals
) {
  paintChrome(ctx, title, ageBands, bandColors);
  const plot = { x: 64, y: TITLE_TOP, w: W - 112, h: H - TITLE_TOP - BOTTOM_RESERVE };
  const months = series.map((s) => s.month);
  const totals = series.map((s) =>
    ageBands.reduce((sum, b) => sum + (Number(s.byBand[b]?.[metric]) || 0), 0)
  );
  const yMax = sceYMax(Math.max(...totals, 0));

  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = plot.y + plot.h - (plot.h * i) / 4;
    ctx.beginPath();
    ctx.moveTo(plot.x, y);
    ctx.lineTo(plot.x + plot.w, y);
    ctx.stroke();
    ctx.fillStyle = MUTED;
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    const label =
      metric === 'avg'
        ? ((yMax * i) / 4).toFixed(1)
        : String(Math.round((yMax * i) / 4));
    ctx.fillText(label, plot.x - 8, y + 4);
  }
  ctx.textAlign = 'left';

  const n = Math.max(months.length, 1);
  const gap = 8;
  const barW = Math.max(8, (plot.w - gap * (n + 1)) / n);

  months.forEach((month, mi) => {
    const x = plot.x + gap + mi * (barW + gap);
    let yBase = plot.y + plot.h;
    ageBands.forEach((band, bi) => {
      const v = Number(series[mi].byBand[band]?.[metric]) || 0;
      const h = (v / yMax) * plot.h;
      ctx.fillStyle = bandColors[bi] || PIE_COLORS[bi % PIE_COLORS.length];
      ctx.fillRect(x, yBase - h, barW, h);
      yBase -= h;
    });
    // Month diagonals stay inside axis band (~44px); above gap+legend
    ctx.fillStyle = MUTED;
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.save();
    ctx.translate(x + barW / 2, plot.y + plot.h + 12);
    ctx.rotate(-Math.PI / 6);
    ctx.textAlign = 'right';
    ctx.fillText(month, 0, 0);
    ctx.restore();
  });
}

function drawMultiLineAvg(
  ctx: CanvasRenderingContext2D,
  title: string,
  series: SceChartMonthPoint[],
  ageBands: string[],
  bandColors: string[]
) {
  paintChrome(ctx, title, ageBands, bandColors);
  const plot = { x: 64, y: TITLE_TOP, w: W - 112, h: H - TITLE_TOP - BOTTOM_RESERVE };
  const months = series.map((s) => s.month);
  let maxV = 0;
  series.forEach((s) => {
    ageBands.forEach((b) => {
      maxV = Math.max(maxV, Number(s.byBand[b]?.avg) || 0);
    });
  });
  const yMax = sceYMax(maxV);

  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = plot.y + plot.h - (plot.h * i) / 4;
    ctx.beginPath();
    ctx.moveTo(plot.x, y);
    ctx.lineTo(plot.x + plot.w, y);
    ctx.stroke();
    ctx.fillStyle = MUTED;
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(((yMax * i) / 4).toFixed(1), plot.x - 8, y + 4);
  }
  ctx.textAlign = 'left';

  const n = Math.max(months.length, 1);
  const step = n === 1 ? 0 : plot.w / (n - 1);

  ageBands.forEach((band, bi) => {
    const color = bandColors[bi] || PIE_COLORS[bi % PIE_COLORS.length];
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    months.forEach((_, mi) => {
      const v = Number(series[mi].byBand[band]?.avg) || 0;
      const x = plot.x + (n === 1 ? plot.w / 2 : mi * step);
      const y = plot.y + plot.h - (v / yMax) * plot.h;
      if (mi === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    months.forEach((_, mi) => {
      const v = Number(series[mi].byBand[band]?.avg) || 0;
      const x = plot.x + (n === 1 ? plot.w / 2 : mi * step);
      const y = plot.y + plot.h - (v / yMax) * plot.h;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 3.5, 0, Math.PI * 2);
      ctx.fill();
    });
  });

  months.forEach((month, mi) => {
    const x = plot.x + (n === 1 ? plot.w / 2 : mi * step);
    ctx.fillStyle = MUTED;
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.save();
    ctx.translate(x, plot.y + plot.h + 12);
    ctx.rotate(-Math.PI / 6);
    ctx.textAlign = 'right';
    ctx.fillText(month, 0, 0);
    ctx.restore();
  });
}

function drawDonut(
  ctx: CanvasRenderingContext2D,
  title: string,
  ageBands: string[],
  bandColors: string[],
  mix: SceChartMixSlice[],
  monthLabel: string
) {
  // Donut ABOVE (centered), legend BELOW — skip bottom age-band chrome legend
  paintChrome(ctx, `${title} · ${monthLabel}`, [], []);
  const cx = W / 2;
  const cy = 240;
  const rOut = 172;
  const rIn = Math.round(rOut * 0.56);
  const total = mix.reduce((s, d) => s + d.value, 0) || 1;

  let angle = -Math.PI / 2;
  mix.forEach((slice) => {
    const sweep = (slice.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.fillStyle = slice.color;
    ctx.arc(cx, cy, rOut, angle, angle + sweep);
    ctx.closePath();
    ctx.fill();
    angle += sweep;
  });

  ctx.beginPath();
  ctx.fillStyle = CARD;
  ctx.arc(cx, cy, rIn, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = TEXT;
  ctx.font = 'bold 22px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(Math.round(total).toLocaleString(), cx, cy - 6);
  ctx.fillStyle = MUTED;
  ctx.font = '11px Inter, system-ui, sans-serif';
  ctx.fillText('Account Mix', cx, cy + 14);
  ctx.textAlign = 'left';

  // Tighter 2-col legend under pie; label · count · pct% on one line
  const legendTop = cy + rOut + 20;
  const cols = mix.length > 12 ? 3 : 2;
  const rowsPerCol = Math.ceil(mix.length / cols) || 1;
  const colGap = 24;
  const colW = (W - 96 - colGap * (cols - 1)) / cols;
  const rowH = 22;
  mix.forEach((slice, i) => {
    const col = Math.floor(i / rowsPerCol);
    const row = i % rowsPerCol;
    const lx = 48 + col * (colW + colGap);
    const ly = legendTop + row * rowH;
    const pct = total > 0 ? (slice.value / total) * 100 : 0;
    ctx.fillStyle = slice.color;
    ctx.fillRect(lx, ly - 8, 12, 12);
    ctx.fillStyle = TEXT;
    ctx.font = '13px Inter, system-ui, sans-serif';
    ctx.fillText(
      `${slice.name} · ${Math.round(slice.value).toLocaleString()} · ${pct.toFixed(1)}%`,
      lx + 18,
      ly
    );
  });
}

async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
  return new Uint8Array(await blob.arrayBuffer());
}

function crc32(buf: Uint8Array): number {
  let c = ~0;
  for (let i = 0; i < buf.length; i++) {
    c ^= buf[i];
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return ~c >>> 0;
}

function u16(n: number): Uint8Array {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
}
function u32(n: number): Uint8Array {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
}
function concat(parts: Uint8Array[]): Uint8Array {
  const len = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function buildStoreZip(files: Array<{ name: string; data: Uint8Array }>): Blob {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  const enc = new TextEncoder();

  for (const f of files) {
    const nameBytes = enc.encode(f.name);
    const crc = crc32(f.data);
    const size = f.data.length;
    const local = concat([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(size),
      u32(size),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      f.data,
    ]);
    const central = concat([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(size),
      u32(size),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }

  const localBlob = concat(locals);
  const centralBlob = concat(centrals);
  const end = concat([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralBlob.length),
    u32(localBlob.length),
    u16(0),
  ]);
  const zipBytes = concat([localBlob, centralBlob, end]);
  const copy = new Uint8Array(zipBytes.byteLength);
  copy.set(zipBytes);
  return new Blob([copy], { type: 'application/zip' });
}

export function buildSceChartsZipFilename(from: string, to: string): string {
  return `SCE_${from}_${to}_charts.zip`;
}

export function buildSceChartPngName(from: string, to: string, n: number | string): string {
  return `SCE_${from}_${to}_G${n}.png`;
}

export async function buildSlotCheckEngagementChartPngZip(input: SceChartPngInput): Promise<Blob> {
  const { ageBands, bandColors, monthSeries, fromMonth, toMonth } = input;

  const jobs: Array<{
    n: number | string;
    height?: number;
    draw: (ctx: CanvasRenderingContext2D) => void;
  }> = [
    {
      n: 1,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G1 · Active Unique Accounts by age', monthSeries, ageBands, bandColors, 'uniques'),
    },
    {
      n: 2,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G2 · Active Logins by age', monthSeries, ageBands, bandColors, 'sessions'),
    },
    {
      n: 3,
      draw: (ctx) => drawMultiLineAvg(ctx, 'G3 · Avg logins by age', monthSeries, ageBands, bandColors),
    },
    {
      n: 4,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G4 · New Accounts by age', monthSeries, ageBands, bandColors, 'created'),
    },
  ];

  jobs.push({
      n: 6,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G6 · Accounts Linked by age', monthSeries, ageBands, bandColors, 'linked'),
    });

  const g5Views = input.g5Views?.length ? input.g5Views : [];
  g5Views.forEach((view) => {
    const rowsGuess = Math.ceil(view.mix.length / (view.mix.length > 12 ? 3 : 2));
    const needH = Math.max(H, 240 + 172 + 20 + rowsGuess * 22 + 40);
    jobs.push({
      n: `5_${view.key}`,
      height: needH,
      draw: (ctx) =>
        drawDonut(ctx, view.title, ageBands, bandColors, view.mix, input.g5Month),
    });
  });

  const files: Array<{ name: string; data: Uint8Array }> = [];
  for (const job of jobs) {
    const canvas = makeCanvas(job.height ?? H);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    job.draw(ctx);
    files.push({
      name: buildSceChartPngName(fromMonth, toMonth, job.n as number | string),
      data: await canvasToPngBytes(canvas),
    });
  }
  return buildStoreZip(files);
}

export async function downloadSlotCheckEngagementChartPngZip(input: SceChartPngInput): Promise<void> {
  const blob = await buildSlotCheckEngagementChartPngZip(input);
  downloadBlob(blob, buildSceChartsZipFilename(input.fromMonth, input.toMonth));
}

/** @deprecated alias */
export const downloadSlotCheckEngagementChartPngs = downloadSlotCheckEngagementChartPngZip;
export const buildSlotCheckEngagementChartPngs = buildSlotCheckEngagementChartPngZip;
